<template>
  <div class="courts-selection">
    <h3 class="courts-title">Выберите зал и длительность</h3>

    <div v-if="courts.length === 0" class="no-courts">
      <p>Нет доступных залов</p>
    </div>

    <div v-else class="courts-list">
      <div
        v-for="court in courts"
        :key="court.id"
        class="court-card"
      >
        <div class="court-header" :class="{ 'court-header--no-image': !hasCourtImages(court) }" @click="openCourtDetails(court)">
          <img v-if="hasCourtImages(court)" :src="court.images[0]" :alt="court.name" class="court-image" />
          <div class="court-info">
            <h4 class="court-name">{{ court.name }}</h4>
            <div class="court-description" v-html="getCourtDescriptionPreview(court.description)"></div>
          </div>
          <button class="arrow-button">
            <img :src="arrowIcon" alt="" class="arrow-icon" />
          </button>
        </div>

        <div class="durations-section">
          <div class="durations-label">Выберите длительность:</div>
          <div class="durations-grid">
            <button
              v-for="duration in court.durations"
              :key="`${court.id}-${duration.minutes}`"
              class="duration-button"
              :class="{ active: isSelectedDuration(court.id, duration.minutes) }"
              @click="selectCourtDuration(court.id, duration.minutes, duration.price, duration.currency, duration.serviceId, duration.trainingId)"
            >
              <div class="duration-time">{{ duration.minutes }} мин</div>
              <div class="duration-price">{{ formatPrice(duration.price, duration.currency) }}</div>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Court Details Modal -->
    <div v-if="selectedCourtForDetails" class="modal-overlay" @click.self="closeCourtDetails">
      <div class="modal-content">
        <h3 class="modal-title">О зале</h3>

        <button class="modal-close" @click="closeCourtDetails">
          <img :src="closeIcon" alt="Close" />
        </button>

        <div v-if="hasCourtImages(selectedCourtForDetails)" class="modal-images-wrapper">
          <div class="modal-images">
            <div class="images-carousel">
              <img
                v-for="(image, index) in selectedCourtForDetails.images"
                :key="index"
                :src="image"
                :alt="`${selectedCourtForDetails.name} - ${index + 1}`"
                class="modal-image"
              />
            </div>
          </div>

          <div class="carousel-controls" v-if="selectedCourtForDetails.images.length > 2">
            <button
              class="carousel-button prev"
              @click="previousImage"
            >
              <img :src="arrowLeftIcon" alt="Previous" />
            </button>

            <button
              class="carousel-button next"
              @click="nextImage"
            >
              <img :src="arrowRightIcon" alt="Next" />
            </button>
          </div>
        </div>

        <div class="modal-description-block">
          <h4 class="description-court-name">{{ selectedCourtForDetails.name }}</h4>

          <div class="description-section">
            <div class="description-label">Описание</div>
            <div
              class="description-text"
              :class="{ collapsed: !isCourtDescriptionExpanded }"
              v-html="getCourtDescriptionPreview(selectedCourtForDetails.description)"
            ></div>
            <button
              v-if="shouldShowCourtDescriptionToggle"
              type="button"
              class="description-toggle"
              @click="isCourtDescriptionExpanded = true"
            >
              Подробнее
            </button>
          </div>
        </div>

        <div class="modal-duration-block">
          <h4 class="duration-block-title">Выберите длительность</h4>
          <div class="duration-datetime">{{ formattedDateTime }}</div>

          <div class="duration-slots">
            <button
              v-for="duration in selectedCourtForDetails.durations"
              :key="`modal-${selectedCourtForDetails.id}-${duration.minutes}`"
              class="duration-slot"
              :class="{ active: isSelectedDurationInModal(duration.minutes) }"
              @click="selectDurationInModal(duration.minutes)"
            >
              <div class="duration-slot-time">{{ duration.minutes }} мин</div>
              <div class="duration-slot-price">{{ formatPrice(duration.price, duration.currency) }}</div>
            </button>
          </div>
        </div>

        <button
          class="modal-book-button"
          @click="handleBooking"
        >
          Забронировать
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { formatCurrencyPrice, pickPreferredCurrency } from '@/widgets/booking/utils/currency'
import closeIcon from '@/widgets/booking/assets/img/icons/booking-close.svg'
import arrowLeftIcon from '@/widgets/booking/assets/img/icons/arrow_left.svg'
import arrowRightIcon from '@/widgets/booking/assets/img/icons/arrow_right.svg'
import arrowIcon from "@/widgets/booking/assets/img/icons/arrow_right.svg";

const props = defineProps({
  courts: {
    type: Array,
    required: true
  },
  currency: {
    type: String,
    default: 'RUB'
  },
  modelValue: {
    type: Object,
    default: undefined
  }
})

const emit = defineEmits(['update:modelValue', 'book'])

const store = useStore()
const selectedCourtForDetails = ref(null)
const currentImageIndex = ref(0)
const selectedDurationInModal = ref(null)
const isCourtDescriptionExpanded = ref(false)

const shouldShowCourtDescriptionToggle = computed(() => {
  const description = selectedCourtForDetails.value?.description || ''
  return description.length > 180 && !isCourtDescriptionExpanded.value
})

const isSelectedDuration = (courtId, minutes) => {
  return props.modelValue?.courtId === courtId && props.modelValue?.duration === minutes
}

const hasCourtImages = (court) => {
  return !!court?.images?.length
}

const resolveDurationCurrency = (currency) => {
  return pickPreferredCurrency(currency, props.currency)
}

const selectCourtDuration = (courtId, minutes, price, currency, serviceId, trainingId) => {
  emit('update:modelValue', {
    courtId,
    duration: minutes,
    price,
    currency: resolveDurationCurrency(currency),
    serviceId,
    trainingId
  })
}

const openCourtDetails = (court) => {
  selectedCourtForDetails.value = court
  currentImageIndex.value = 0
  isCourtDescriptionExpanded.value = false
  // Pre-select current duration if this court is selected
  if (props.modelValue?.courtId === court.id) {
    selectedDurationInModal.value = props.modelValue.duration
  } else {
    selectedDurationInModal.value = null
  }
}

const closeCourtDetails = () => {
  selectedCourtForDetails.value = null
  currentImageIndex.value = 0
  selectedDurationInModal.value = null
  isCourtDescriptionExpanded.value = false
}

const previousImage = () => {
  const carousel = document.querySelector('.images-carousel')
  if (carousel) {
    const scrollAmount = 211 + 12 // image width + gap
    carousel.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
    if (currentImageIndex.value > 0) {
      currentImageIndex.value--
    }
  }
}

const nextImage = () => {
  const carousel = document.querySelector('.images-carousel')
  if (carousel) {
    const scrollAmount = 211 + 12 // image width + gap
    carousel.scrollBy({ left: scrollAmount, behavior: 'smooth' })
    if (selectedCourtForDetails.value && currentImageIndex.value < selectedCourtForDetails.value.images.length - 1) {
      currentImageIndex.value++
    }
  }
}

const formatPrice = (price, currency = props.currency) => {
  return formatCurrencyPrice(price, resolveDurationCurrency(currency))
}

const getCourtDescription = (description) => {
  return description || 'Описание отсутствует'
}

const getCourtDescriptionPreview = (description) => {
  const normalizedDescription = getCourtDescription(description)

  return normalizedDescription
    .replace(/<\/p>\s*<p>/gi, '<br><br>')
    .replace(/^<p>/i, '')
    .replace(/<\/p>$/i, '')
}

const formattedDateTime = computed(() => {
  const selectedDate = store.state.selectedDate
  const selectedSlot = store.getters.selectedSlot

  if (!selectedDate || !selectedSlot) return ''

  const date = new Date(selectedDate)
  const weekday = date.toLocaleDateString('ru-RU', { weekday: 'short' }).replace('.', '')

  // Get day and month in genitive case (родительный падеж)
  const dayMonth = date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })

  return `${selectedSlot.time} • ${weekday}, ${dayMonth}`
})

const isSelectedDurationInModal = (minutes) => {
  return selectedDurationInModal.value === minutes
}

const selectDurationInModal = (minutes) => {
  selectedDurationInModal.value = minutes
}

const handleBooking = () => {
  if (!selectedCourtForDetails.value || !selectedDurationInModal.value) return

  // Find the price for selected duration
  const duration = selectedCourtForDetails.value.durations.find(d => d.minutes === selectedDurationInModal.value)
  if (!duration) return

  // Update the selection
  emit('update:modelValue', {
    courtId: selectedCourtForDetails.value.id,
    duration: selectedDurationInModal.value,
    price: duration.price,
    currency: resolveDurationCurrency(duration.currency),
    serviceId: duration.serviceId,
    trainingId: duration.trainingId
  })

  // Close modal
  closeCourtDetails()

  // Trigger common booking flow in parent
  emit('book')
}
</script>

<style scoped lang="scss">
.courts-selection {
  background: white;
  border-radius: 12px;
  margin-bottom: 16px;
}

.courts-title {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 20px;
  color: #252525;
  margin: 0 0 16px 0;
}

.no-courts {
  padding: 32px;
  text-align: center;

  p {
    font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 14px;
    color: #757885;
    margin: 0;
  }
}

.courts-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.court-card {
  background: #F3F5F8;
  border-radius: 12px;
  padding: 16px;
}

.court-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  cursor: pointer;
}

.court-header--no-image .court-info {
  padding-left: 0;
}

.court-image {
  width: 52px;
  height: 52px;
  border-radius: 48px;
  object-fit: cover;
  flex-shrink: 0;
}

.court-info {
  flex: 1;
  min-width: 0;
}

.court-name {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  color: #333333;
  margin: 0 0 4px 0;
}

.court-description {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: #333333;
  margin: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
  word-break: break-word;
  white-space: normal;
}

.court-description :deep(*) {
  font: inherit;
  color: inherit;
}

.court-description :deep(p) {
  margin: 0;
  display: inline;
}

.court-description :deep(strong) {
  font-weight: 700;
}

.court-description :deep(em) {
  font-style: italic;
}

.court-description :deep(u) {
  text-decoration: underline;
}

.court-description :deep(s) {
  text-decoration: line-through;
}

.arrow-button {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #FFFFFF;
  border-radius: 7px;
  flex-shrink: 0;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
}

.durations-section {
  margin-top: 12px;
}

.durations-label {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: #757885;
  margin-bottom: 8px;
}

.durations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(71px, 1fr));
  gap: 8px;
}

.duration-button {
  width: 71px;
  height: 60px;
  background: #FFFFFF;
  border: none;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: darken(#FFFFFF, 3%);
  }

  &.active {
    background: var(--booking-widget-primary-color, #3C67D6);

    .duration-time,
    .duration-price {
      color: #FFFFFF;
    }
  }
}

.duration-time {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
  color: #333333;
}

.duration-price {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
  color: #757885;
}

// Modal styles
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  position: relative;
  max-width: 500px;
  width: 100%;
  max-height: 95vh;
  background: white;
  border-radius: 30px;
  overflow: hidden;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.modal-title {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  color: #333333;
  margin: 0;
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F3F5F8;
  border-radius: 7px;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s ease;
  z-index: 10;

  &:hover {
    opacity: 0.8;
  }

  img {
    width: 14px;
    height: 14px;
  }
}

.modal-images-wrapper {
  position: relative;
  width: calc(100% + 48px);
  margin-left: -24px;
  margin-right: -24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.modal-images {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.images-carousel {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 0 24px;

  &::-webkit-scrollbar {
    display: none;
  }
}

.modal-image {
  width: 211px;
  height: 148px;
  border-radius: 12px;
  object-fit: cover;
  flex-shrink: 0;
}

.carousel-controls {
  display: flex;
  gap: 8px;
  align-items: center;
  padding-left: 24px;
}

.carousel-button {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F3F5F8;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: darken(#F3F5F8, 5%);
  }

  img {
    width: 12px;
    height: 12px;
  }
}

.modal-description-block {
  width: 100%;
  background: #F3F5F8;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.description-court-name {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 20px;
  color: #333333;
  margin: 0;
}

.description-section,
.amenities-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.description-label {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #757885;
}

.description-text {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: #333333;
  margin: 0;
  word-break: break-word;
}

.description-text :deep(*) {
  color: inherit;
}

.description-text :deep(p) {
  margin: 0 0 8px;
}

.description-text :deep(p:last-child) {
  margin-bottom: 0;
}

.description-text.collapsed {
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.description-toggle {
  width: fit-content;
  margin-top: 8px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--booking-widget-primary-color, #3C67D6);
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;
}

.amenities-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.amenity-item {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 6px 12px;
  background: var(--booking-widget-primary-color-soft, #3C67D61A);
  border-radius: 12px;
}

.amenity-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  object-fit: contain;
}

.amenity-name {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
  color: var(--booking-widget-primary-color, #3C67D6);
}

.modal-duration-block {
  width: 100%;
  background: #F3F5F8;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.duration-block-title {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 20px;
  color: #333333;
  margin: 0;
}

.duration-datetime {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: #757885;
  margin-bottom: 8px;
}

.duration-slots {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.duration-slot {
  width: 71px;
  height: 60px;
  background: #FFFFFF;
  border: none;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: darken(#FFFFFF, 3%);
  }

  &.active {
    background: var(--booking-widget-primary-color, #3C67D6);

    .duration-slot-time,
    .duration-slot-price {
      color: #FFFFFF;
    }
  }
}

.duration-slot-time {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
  color: #333333;
}

.duration-slot-price {
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
  color: #757885;
}

.modal-book-button {
  width: 100%;
  background: var(--booking-widget-primary-color, #3C67D6);
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  color: #FFFFFF;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.85;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
}
</style>
